import { useCallback, useEffect, useState } from 'react';
import type { QualityOperationsSnapshotV1, QualityPlatformControlV1 } from '@djgg/creations';
import { useServices } from '../services/context.js';

const commandId = (): string => `quality-admin-${Date.now()}-${crypto.randomUUID()}`;
const money = (micros: number): string => `$${(micros / 1_000_000).toFixed(2)}`;

export function QualityV2Panel() {
  const services = useServices();
  const [snapshot, setSnapshot] = useState<QualityOperationsSnapshotV1 | null>(null);
  const [readiness, setReadiness] = useState<Awaited<ReturnType<typeof services.callable.getQualityOperations>>['readiness'] | null>(null);
  const [policy, setPolicy] = useState<Awaited<ReturnType<typeof services.callable.getQualityOperations>>['policy'] | null>(null);
  const [control, setControl] = useState<QualityPlatformControlV1 | null>(null);
  const [draft, setDraft] = useState<QualityPlatformControlV1 | null>(null);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setMessage('');
    try {
      const result = await services.callable.getQualityOperations({});
      setSnapshot(result.snapshot);
      setReadiness(result.readiness);
      setPolicy(result.policy);
      setControl(result.snapshot.control ? {
        ...result.snapshot.control,
        commandId: '', reason: '', updatedBy: '',
      } : null);
      setDraft(result.snapshot.control ? {
        ...result.snapshot.control,
        commandId: '', reason: '', updatedBy: '',
      } : null);
    } catch {
      setMessage('V2 controls could not be loaded. No rollout change is available.');
    }
  }, [services]);

  useEffect(() => { void load(); }, [load]);

  async function initialize() {
    setBusy(true);
    setMessage('');
    try {
      await services.callable.initializeQualityControl({
        commandId: commandId(),
        reason: reason.trim() || 'Initialize V2 rollout authority in the safe off state',
      });
      setReason('');
      await load();
      setMessage('V2 controls initialized off and fully paused.');
    } catch {
      setMessage('Initialization failed. Reload the live state before retrying.');
    } finally {
      setBusy(false);
    }
  }

  async function save(next = draft) {
    if (!control || !next || reason.trim().length < 8) {
      setMessage('Enter a reason of at least 8 characters.');
      return;
    }
    const enabling = next.admissionMode !== 'off' && !next.acceptancePaused;
    if (enabling && (!readiness?.reachable || !readiness.capable
      || readiness.policyRevision !== control.policyRevision || readiness.recipe !== 'verified-core@2')) {
      setMessage('The deployed builder capability or policy does not match. Enablement is blocked.');
      return;
    }
    if (enabling && confirmation !== 'ENABLE V2') {
      setMessage('Type ENABLE V2 to confirm paid provider admission.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await services.callable.updateQualityControl({
        expectedRevision: control.revision,
        commandId: commandId(),
        admissionMode: next.admissionMode,
        acceptancePaused: next.acceptancePaused,
        providerDispatchPaused: next.providerDispatchPaused,
        publicationPaused: next.publicationPaused,
        previewGrantsPaused: next.previewGrantsPaused,
        reason: reason.trim(),
      });
      setReason('');
      setConfirmation('');
      await load();
      setMessage('V2 controls updated.');
    } catch {
      setMessage('The control revision changed or the update failed. Live values were reloaded.');
      await load();
    } finally {
      setBusy(false);
    }
  }

  const active = Boolean(control && control.admissionMode !== 'off' && !control.acceptancePaused);
  return (
    <div className="panel" data-testid="quality-v2-panel">
      <h2 className="panel__title">Verified game quality V2</h2>
      <p className="muted">
        Historical V2 admissions are {active ? `ON (${control?.admissionMode})` : 'OFF'}. Turning admission
        off does not strand accepted work; capable workers keep draining it. Fresh games always use complete game creation; these controls do not select their recipe.
      </p>
      <p className="muted" role="status">
        {snapshot
          ? `Runs ${Object.values(snapshot.runsByPhase).reduce((sum, value) => sum + value, 0)} · pending exposure ${money(snapshot.reservedOrUncertainExposureMicros)} · settled ${money(snapshot.settledCostMicros)}`
          : 'Loading V2 operations…'}
      </p>
      <p className="muted">
        Builder: {readiness?.reachable && readiness.capable
          ? `ready (${readiness.appVersion ?? 'unknown version'}, ${readiness.recipe ?? 'unknown recipe'})`
          : 'not ready — enabling is blocked'}
      </p>
      {message && <p role="alert">{message}</p>}
      {snapshot && !control && (
        <div>
          <p>No rollout authority exists yet. Initialization is safe: Off with every emergency gate paused.</p>
          <button className="btn btn--primary" type="button" disabled={busy} onClick={() => void initialize()}>
            Initialize V2 controls
          </button>
        </div>
      )}
      {control && draft && (
        <>
          <div className="field">
            <label className="field__label" htmlFor="quality-v2-mode">New admission cohort</label>
            <select
              id="quality-v2-mode"
              className="select"
              value={draft.admissionMode}
              onChange={(event) => setDraft({ ...draft, admissionMode: event.target.value as QualityPlatformControlV1['admissionMode'] })}
            >
              <option value="off">Off</option>
              <option value="admins">Admins only</option>
              <option value="all">All creators</option>
            </select>
          </div>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {([
              ['acceptancePaused', 'Pause new admissions'],
              ['providerDispatchPaused', 'Pause provider dispatch'],
              ['publicationPaused', 'Pause publication'],
              ['previewGrantsPaused', 'Pause candidate previews'],
            ] as const).map(([key, label]) => (
              <label className="field" style={{ flex: '1 1 260px' }} key={key}>
                <input type="checkbox" checked={draft[key]} onChange={(event) => setDraft({ ...draft, [key]: event.target.checked })} />{' '}
                {label}
              </label>
            ))}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="quality-v2-reason">Reason (recorded in immutable history)</label>
            <input id="quality-v2-reason" className="input" value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
          {draft.admissionMode !== 'off' && !draft.acceptancePaused && (
            <div className="field">
              <label className="field__label" htmlFor="quality-v2-confirm">Type ENABLE V2</label>
              <input id="quality-v2-confirm" className="input" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
              <p className="muted">
                This authorizes bounded paid code-authoring calls, up to {money(policy?.maxRunExposureMicros ?? 0)} exposure per admitted run.
                V2 media generation remains disabled.
              </p>
            </div>
          )}
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn--primary" type="button" disabled={busy} onClick={() => void save()}>
              Apply V2 controls
            </button>
            <button className="btn" type="button" disabled={busy} onClick={() => void save({ ...draft, admissionMode: 'off', acceptancePaused: true })}>
              Stop new admissions
            </button>
            <button className="btn btn--danger" type="button" disabled={busy} onClick={() => void save({
              ...draft, admissionMode: 'off', acceptancePaused: true, providerDispatchPaused: true,
              publicationPaused: true, previewGrantsPaused: true,
            })}>
              Emergency stop
            </button>
          </div>
          <p className="muted">
            Revision {control.revision} · policy {control.policyRevision} · updated {new Date(control.updatedAt).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}
